import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, fmtUSD, fmtDate, SITUATIONS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, ShieldCheck, AlertTriangle } from "lucide-react";

export default function InvestorDashboard() {
  const { user } = useAuth();
  const [opps, setOpps] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        if (user?.is_approved) {
          const { data: o } = await api.get("/properties/marketplace");
          setOpps(o);
        }
        const { data: my } = await api.get("/offers/mine");
        setOffers(my);
      } catch {}
      setLoading(false);
    })();
  }, [user]);

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow">Provider dashboard</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-2">{user?.name || "Investor"}</h1>
          <div className="mt-2 flex gap-2 items-center">
            {user?.is_approved ? (
              <span className="pill pill-verified"><ShieldCheck size={12} /> Verified</span>
            ) : (
              <span className="pill pill-pending"><AlertTriangle size={12} /> Pending approval</span>
            )}
            <span className="text-xs text-[var(--adn-text-muted)] uppercase tracking-wider">{user?.role}</span>
          </div>
        </div>
        <Link to="/opportunities" data-testid="id-opps" className="btn-gold px-5 py-3 rounded text-sm inline-flex items-center gap-2 self-start">
          <Search size={16} /> Browse Opportunities
        </Link>
      </div>

      {!user?.is_approved && (
        <div className="mt-6 bg-white border-l-4 border-l-[var(--adn-gold)] border-y border-r border-[var(--adn-line)] p-5">
          <p className="font-display font-semibold">Your account is pending verification</p>
          <p className="text-sm text-[var(--adn-text-soft)] mt-1">Upload proof of funds and complete your profile to be approved. Once approved, you'll see live opportunities.</p>
          <Link to="/profile" className="mt-3 inline-flex btn-outline-navy px-4 py-2 rounded text-sm">Complete Profile</Link>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 bg-white border border-[var(--adn-line)] p-6">
          <div className="flex items-center justify-between">
            <p className="eyebrow">Latest opportunities</p>
            <Link to="/opportunities" className="text-xs text-[var(--adn-text-soft)] hover:text-[var(--adn-navy)]">View all →</Link>
          </div>
          {loading ? (
            <p className="mt-4 text-sm text-[var(--adn-text-soft)]">Loading…</p>
          ) : opps.length === 0 ? (
            <p className="mt-4 text-sm text-[var(--adn-text-soft)]">No opportunities matching yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-[var(--adn-line)]">
              {opps.slice(0, 5).map((p) => (
                <li key={p.property_id} className="py-4">
                  <Link to={`/properties/${p.property_id}`} className="block hover:bg-slate-50 -mx-2 px-2 rounded">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-display font-semibold">{p.city}, {p.state}</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {(p.situations || []).slice(0, 2).map((s) => {
                            const meta = SITUATIONS.find(x => x.id === s);
                            return <span key={s} className={`pill ${meta?.pill || "pill-neutral"}`}>{meta?.label || s}</span>;
                          })}
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <div className="font-display tabular text-[var(--adn-navy)]">{fmtUSD(p.estimated_value)}</div>
                        <div className="text-[10px] text-[var(--adn-text-muted)] uppercase tracking-wider">est. value</div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white border border-[var(--adn-line)] p-6">
          <p className="eyebrow">My offers</p>
          {offers.length === 0 ? (
            <p className="mt-3 text-sm text-[var(--adn-text-soft)]">You haven't submitted any offers yet.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {offers.slice(0, 6).map((o) => (
                <li key={o.offer_id} className="border border-[var(--adn-line)] p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{o.offer_type}</span>
                    <span className={`pill ${o.status === "accepted" ? "pill-accepted" : o.status === "rejected" ? "pill-rejected" : "pill-pending"}`}>{o.status}</span>
                  </div>
                  <div className="text-xs text-[var(--adn-text-soft)] mt-1 flex justify-between">
                    <span>{fmtUSD(o.amount)}</span>
                    <span>{fmtDate(o.created_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
